/**
 * Shared chrome for every portal (admin, trader, IB, manager, platform).
 *
 * Floating sidebar that collapses to an icon rail (remembered per portal),
 * grouped nav sections, hover flyouts for branches while collapsed, and a
 * mobile drawer. The flattened nav is published on ShellNavContext so the
 * Topbar's ⌘K palette can search every page the user can actually reach.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { NavLink, Outlet, useLocation, type NavLinkRenderProps } from "react-router-dom";
import { ChevronRight, PanelLeftClose, PanelLeftOpen, X, type LucideIcon } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { Brand } from "./Brand";
import { Topbar } from "./Topbar";
import { PageErrorBoundary } from "./PageErrorBoundary";
import { cn } from "@/lib/utils";
import { t } from "@/lib/i18n";

export interface ShellNavItem {
  label: string;
  to?: string;
  icon?: LucideIcon;
  badge?: string;
  /** Match the route exactly (for index-like dashboards). */
  end?: boolean;
  children?: { label: string; to?: string }[];
}

export interface FlatNavEntry {
  label: string;
  to: string;
  group?: string;
  icon?: LucideIcon;
}

const ShellNavContext = createContext<FlatNavEntry[]>([]);
export const useShellNav = () => useContext(ShellNavContext);

interface AppShellProps {
  nav: ShellNavItem[];
  /** Section heading for a top-level item; consecutive items with the same
   *  value are grouped under one heading. */
  sectionOf?: (item: ShellNavItem) => string | undefined;
  /** Card pinned to the bottom of the sidebar. */
  footer?: React.ReactNode;
  /** Remembers the collapsed state per portal. */
  storageKey: string;
  defaultCollapsed?: boolean;
  maxWidth?: string;
  prefetch?: (to: string) => void;
}

function readBool(key: string, fallback: boolean) {
  try {
    const v = localStorage.getItem(key);
    return v == null ? fallback : v === "1";
  } catch {
    return fallback;
  }
}

export function AppShell({
  nav,
  sectionOf,
  footer,
  storageKey,
  defaultCollapsed = false,
  maxWidth = "max-w-[1560px]",
  prefetch,
}: AppShellProps) {
  const [collapsed, setCollapsed] = useState(() => readBool(`axfx.shell.${storageKey}.rail`, defaultCollapsed));
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    try { localStorage.setItem(`axfx.shell.${storageKey}.rail`, collapsed ? "1" : "0"); } catch { /* private mode */ }
  }, [collapsed, storageKey]);

  // Close the drawer on navigation.
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const flat = useMemo<FlatNavEntry[]>(() => {
    const out: FlatNavEntry[] = [];
    for (const item of nav) {
      if (item.to) out.push({ label: t(item.label), to: item.to, group: sectionOf?.(item), icon: item.icon });
      for (const c of item.children ?? []) {
        if (c.to) out.push({ label: t(c.label), to: c.to, group: t(item.label), icon: item.icon });
      }
    }
    return out;
  }, [nav, sectionOf]);

  const sidebar = (rail: boolean, onClose?: () => void) => (
    <SidebarBody
      nav={nav}
      sectionOf={sectionOf}
      rail={rail}
      footer={footer}
      prefetch={prefetch}
      onToggle={onClose ?? (() => setCollapsed((v) => !v))}
      mobile={!!onClose}
    />
  );

  return (
    <ShellNavContext.Provider value={flat}>
      <div className="min-h-screen flex">
        {/* Desktop */}
        <aside
          className={cn(
            "hidden lg:block sticky top-0 h-screen shrink-0 p-3 pr-0 transition-[width] duration-300 ease-[cubic-bezier(.16,1,.3,1)]",
            collapsed ? "w-[88px]" : "w-[284px]",
          )}
        >
          {sidebar(collapsed)}
        </aside>

        {/* Mobile drawer */}
        <AnimatePresence>
          {mobileOpen && (
            <>
              <motion.div
                className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setMobileOpen(false)}
                aria-hidden
              />
              <motion.aside
                className="fixed inset-y-0 left-0 z-50 w-[292px] max-w-[86vw] p-2.5 lg:hidden"
                initial={{ x: "-104%" }}
                animate={{ x: 0 }}
                exit={{ x: "-104%" }}
                transition={{ type: "spring", stiffness: 420, damping: 40 }}
              >
                {sidebar(false, () => setMobileOpen(false))}
              </motion.aside>
            </>
          )}
        </AnimatePresence>

        <div className="flex-1 flex flex-col min-w-0">
          <Topbar onMenuToggle={() => setMobileOpen(true)} />
          <main className={cn("flex-1 w-full mx-auto px-4 pb-10 pt-5 sm:px-6 lg:px-8 lg:pt-6", maxWidth)}>
            <div key={location.pathname} className="animate-fade-in">
              <PageErrorBoundary>
                <Outlet />
              </PageErrorBoundary>
            </div>
          </main>
        </div>
      </div>
    </ShellNavContext.Provider>
  );
}

/* ─── Sidebar ──────────────────────────────────────────────────────────── */

function SidebarBody({
  nav, sectionOf, rail, footer, prefetch, onToggle, mobile,
}: {
  nav: ShellNavItem[];
  sectionOf?: (item: ShellNavItem) => string | undefined;
  rail: boolean;
  footer?: React.ReactNode;
  prefetch?: (to: string) => void;
  onToggle: () => void;
  mobile: boolean;
}) {
  // Group consecutive items that share a section heading.
  const groups = useMemo(() => {
    const out: { section?: string; items: ShellNavItem[] }[] = [];
    for (const item of nav) {
      const section = sectionOf?.(item);
      const last = out[out.length - 1];
      if (last && last.section === section) last.items.push(item);
      else out.push({ section, items: [item] });
    }
    return out;
  }, [nav, sectionOf]);

  return (
    <div className="glass relative flex h-full flex-col overflow-hidden rounded-[22px]">
      {/* lit top edge in the brand hue */}
      <span className="pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" aria-hidden />

      <div className={cn("flex h-[68px] shrink-0 items-center gap-2", rail ? "justify-center px-2" : "justify-between pl-5 pr-3")}>
        {!rail && <Brand />}
        <button
          type="button"
          onClick={onToggle}
          className="grid h-8 w-8 place-items-center rounded-lg text-fg-subtle transition hover:bg-fg/[0.06] hover:text-fg"
          aria-label={mobile ? "Close menu" : rail ? "Expand sidebar" : "Collapse sidebar"}
          title={mobile ? "Close" : rail ? "Expand" : "Collapse"}
        >
          {mobile ? <X className="h-[18px] w-[18px]" /> : rail ? <PanelLeftOpen className="h-[18px] w-[18px]" /> : <PanelLeftClose className="h-[18px] w-[18px]" />}
        </button>
      </div>

      <div className="mx-4 h-px bg-border/70" />

      <nav className={cn("flex-1 overflow-y-auto overflow-x-hidden py-3 [scrollbar-width:thin]", rail ? "px-2.5" : "px-3")}>
        {groups.map((g, gi) => (
          <div key={gi} className={cn(gi > 0 && "mt-4")}>
            {g.section && !rail && (
              <p className="eyebrow px-3 pb-1.5 !text-[10px] !tracking-[0.16em] text-fg-subtle/80">{t(g.section)}</p>
            )}
            {g.section && rail && gi > 0 && <div className="mx-3 mb-3 h-px bg-border/60" />}
            <ul className="space-y-0.5">
              {g.items.map((item) => (
                <li key={item.label}>
                  <NavEntry item={item} rail={rail} prefetch={prefetch} />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      {footer && !rail && <div className="shrink-0 p-3 pt-0">{footer}</div>}
    </div>
  );
}

const itemBase =
  "group/item relative flex w-full items-center gap-3 rounded-xl text-[13.5px] font-medium transition-colors duration-150";

function ActiveBar() {
  return (
    <motion.span
      layoutId="shell-active"
      className="absolute inset-0 rounded-xl bg-[linear-gradient(90deg,hsl(var(--primary)/0.16),hsl(var(--primary)/0.04))] ring-1 ring-inset ring-primary/20"
      transition={{ type: "spring", stiffness: 500, damping: 40 }}
    >
      <span className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-primary shadow-[0_0_12px_hsl(var(--primary)/0.8)]" />
    </motion.span>
  );
}

function NavEntry({ item, rail, prefetch }: { item: ShellNavItem; rail: boolean; prefetch?: (to: string) => void }) {
  const location = useLocation();
  const Icon = item.icon;
  const hasChildren = !!item.children?.length;
  const childActive =
    hasChildren && item.children!.some((c) => c.to && (location.pathname === c.to || location.pathname.startsWith(c.to + "/")));
  const [open, setOpen] = useState(childActive);
  const [flyout, setFlyout] = useState(false);
  const [anchor, setAnchor] = useState<{ top: number; left: number } | null>(null);
  const closeTimer = useRef<number | undefined>(undefined);
  const btnRef = useRef<HTMLButtonElement>(null);
  const openFlyout = () => {
    window.clearTimeout(closeTimer.current);
    const r = btnRef.current?.getBoundingClientRect();
    if (r) setAnchor({ top: Math.min(r.top - 6, window.innerHeight - 320), left: r.right + 14 });
    setFlyout(true);
  };
  const closeFlyout = () => { closeTimer.current = window.setTimeout(() => setFlyout(false), 140); };

  useEffect(() => { if (childActive) setOpen(true); }, [childActive]);

  const warm = useCallback(() => {
    if (!prefetch) return;
    if (item.to) prefetch(item.to);
    item.children?.forEach((c) => c.to && prefetch(c.to));
  }, [item, prefetch]);

  const icon = Icon ? (
    <Icon className="relative z-10 h-[18px] w-[18px] shrink-0 transition-colors" strokeWidth={1.85} />
  ) : (
    <span className="relative z-10 h-1.5 w-1.5 shrink-0 rounded-full bg-current" />
  );

  /* Leaf */
  if (!hasChildren && item.to) {
    return (
      <NavLink
        to={item.to}
        end={item.end}
        onMouseEnter={warm}
        title={rail ? t(item.label) : undefined}
        className={({ isActive }: NavLinkRenderProps) =>
          cn(
            itemBase,
            rail ? "h-11 justify-center" : "h-10 px-3",
            isActive ? "text-fg" : "text-fg-muted hover:text-fg hover:bg-fg/[0.045]",
          )
        }
      >
        {({ isActive }: NavLinkRenderProps) => (
          <>
            {isActive && <ActiveBar />}
            <span className={cn("contents", isActive && "[&>svg]:text-primary")}>{icon}</span>
            {!rail && <span className="relative z-10 truncate">{t(item.label)}</span>}
            {!rail && item.badge && (
              <span className="relative z-10 ml-auto rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-bold text-primary-foreground num">
                {item.badge}
              </span>
            )}
          </>
        )}
      </NavLink>
    );
  }

  /* Branch — collapsed rail shows a hover flyout */
  if (rail) {
    return (
      <div className="relative" onMouseEnter={() => { openFlyout(); warm(); }} onMouseLeave={closeFlyout}>
        <button
          ref={btnRef}
          type="button"
          aria-label={t(item.label)}
          aria-haspopup="menu"
          aria-expanded={flyout}
          onFocus={openFlyout}
          className={cn(itemBase, "h-11 justify-center", childActive ? "text-fg" : "text-fg-muted hover:text-fg hover:bg-fg/[0.045]")}
        >
          {childActive && <ActiveBar />}
          <span className={cn("contents", childActive && "[&>svg]:text-primary")}>{icon}</span>
        </button>
        {createPortal(
        <AnimatePresence>
          {flyout && anchor && (
            <motion.div
              role="menu"
              onMouseEnter={() => window.clearTimeout(closeTimer.current)}
              onMouseLeave={closeFlyout}
              initial={{ opacity: 0, x: -6 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -6 }}
              transition={{ duration: 0.14 }}
              style={{ top: anchor.top, left: anchor.left }}
              className="fixed z-[60] w-60 rounded-2xl border border-border bg-bg-elevated p-1.5 shadow-pop before:absolute before:-left-4 before:inset-y-0 before:w-4"
            >
              <p className="eyebrow px-2.5 pb-1 pt-1.5">{t(item.label)}</p>
              {item.children!.map((c) =>
                c.to ? (
                  <NavLink
                    key={c.label}
                    to={c.to}
                    onClick={() => setFlyout(false)}
                    className={({ isActive }: NavLinkRenderProps) =>
                      cn(
                        "block rounded-lg px-2.5 py-2 text-[13px] transition-colors",
                        isActive ? "bg-primary/12 text-primary font-medium" : "text-fg-muted hover:bg-fg/[0.05] hover:text-fg",
                      )
                    }
                  >
                    {t(c.label)}
                  </NavLink>
                ) : null,
              )}
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
        )}
      </div>
    );
  }

  /* Branch — expanded accordion */
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        onMouseEnter={warm}
        aria-expanded={open}
        className={cn(itemBase, "h-10 px-3", childActive ? "text-fg" : "text-fg-muted hover:text-fg hover:bg-fg/[0.045]")}
      >
        <span className={cn("contents", childActive && "[&>svg]:text-primary")}>{icon}</span>
        <span className="flex-1 truncate text-left">{t(item.label)}</span>
        {childActive && !open && <span className="h-1.5 w-1.5 rounded-full bg-primary" aria-hidden />}
        <ChevronRight className={cn("h-3.5 w-3.5 shrink-0 text-fg-subtle transition-transform duration-200", open && "rotate-90 text-fg-muted")} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.ul
            key="sub"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <li className="relative ml-[21px] mt-0.5 mb-1.5 border-l border-border/80 pl-3">
              <ul className="space-y-px">
                {item.children!.map((c) =>
                  c.to ? (
                    <li key={c.label}>
                      <NavLink
                        to={c.to}
                        onMouseEnter={() => prefetch?.(c.to!)}
                        className={({ isActive }: NavLinkRenderProps) =>
                          cn(
                            "relative block rounded-lg px-3 py-[7px] text-[13px] transition-colors",
                            isActive
                              ? "bg-primary/10 text-primary font-medium before:absolute before:-left-[13px] before:top-1/2 before:h-4 before:w-px before:-translate-y-1/2 before:bg-primary"
                              : "text-fg-subtle hover:text-fg hover:bg-fg/[0.04]",
                          )
                        }
                      >
                        {t(c.label)}
                      </NavLink>
                    </li>
                  ) : null,
                )}
              </ul>
            </li>
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}

/** Small identity card for the sidebar footer. */
export function ShellFooterCard({ title, subtitle, icon: Icon }: { title: string; subtitle: string; icon?: LucideIcon }) {
  return (
    <div className="relative overflow-hidden rounded-2xl ring-gold p-3.5">
      <div className="pointer-events-none absolute -right-6 -top-10 h-24 w-24 rounded-full bg-primary/25 blur-2xl" aria-hidden />
      <div className="relative flex items-center gap-3">
        {Icon && (
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-primary/15 text-primary">
            <Icon className="h-[18px] w-[18px]" />
          </span>
        )}
        <div className="min-w-0">
          <p className="text-[13px] font-semibold text-fg truncate">{title}</p>
          <p className="text-[11.5px] text-fg-subtle truncate">{subtitle}</p>
        </div>
      </div>
    </div>
  );
}

/** Placeholder chrome while /auth/me resolves — same geometry as the shell. */
export function ShellSkeleton({ items = 10 }: { items?: number }) {
  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:block w-[284px] shrink-0 p-3 pr-0">
        <div className="glass h-[calc(100vh-24px)] rounded-[22px] p-4 space-y-2.5">
          <div className="skeleton h-9 w-36 mb-5" />
          {Array.from({ length: items }).map((_, i) => (
            <div key={i} className="skeleton h-9 w-full" style={{ opacity: 1 - i * 0.06 }} />
          ))}
        </div>
      </div>
      <div className="flex-1 px-4 pt-3 sm:px-6 lg:px-8 space-y-6">
        <div className="glass h-[60px] rounded-2xl" />
        <div className="skeleton h-12 w-72" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => <div key={i} className="skeleton h-32 rounded-2xl" />)}
        </div>
        <div className="skeleton h-80 rounded-2xl" />
      </div>
    </div>
  );
}

/** Stable-sorts nav items by the order their section appears in `order`. */
export function orderBySection<T extends ShellNavItem>(items: T[], sectionOf: (i: T) => string | undefined, order: string[]): T[] {
  const rank = (i: T) => {
    const r = order.indexOf(sectionOf(i) ?? "");
    return r === -1 ? order.length : r;
  };
  return items.map((it, idx) => ({ it, idx })).sort((a, b) => rank(a.it) - rank(b.it) || a.idx - b.idx).map((x) => x.it);
}
